import { Avatar, Button, Flex, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { RightOutlined } from '@ant-design/icons';
import { CardContent, CardShellEcho } from './common';
import { formatCurrency } from '@/utils/currencyUtils';
import NoDataMessageEcho from '../states/NoDataMessageEcho';
import { NetworkShowSummary } from '@/types';
import ShowPerformanceModalEcho from '../modals/ShowPerformanceModal/ShowPerformanceModalEcho';
import { useShowsSummaryQuery } from '@/services/revenueApi';
import useFetchQuery from '@/hooks/useFetchQuery';
import { MIXPANEL_EVENT, RevenueDashboardWidget } from '@/utils/mixpanelUtils/events';
import { mixpanelTrack } from '@/utils/mixpanelUtils/asyncMixpanel';

const ShowPerformanceCardEcho = ({ className }: { className?: string }) => {
  const { queryResult: showsSummary } = useFetchQuery(useShowsSummaryQuery);

  return (
    <CardShellEcho
      className={className}
      style={{ display: 'flex', flexDirection: 'column' }}
      testId="show-performance-card"
      title="Show performance"
      headerOptions={
        <ShowPerformanceModalEcho
          renderTrigger={(openModal, startPrefetch) => (
            <Button
              variant="text"
              color="primary"
              iconPlacement="end"
              icon={<RightOutlined />}
              onClick={() => {
                openModal();
                mixpanelTrack(MIXPANEL_EVENT.earnings.showPerformance.moreDetailsClicked, {
                  widget: RevenueDashboardWidget.ShowPerformance,
                  page: 'earnings',
                });
              }}
              onMouseEnter={startPrefetch}
            >
              More details
            </Button>
          )}
        />
      }
    >
      <CardContent {...showsSummary} renderData={(data) => <Data data={data} />} />
    </CardShellEcho>
  );
};

const Data = ({ data }: { data: NetworkShowSummary[] }) => {
  if (data.length === 0) {
    return <NoDataMessageEcho dataType="shows" />;
  }

  const columns: ColumnsType<NetworkShowSummary> = [
    {
      title: 'Show',
      dataIndex: 'name',
      key: 'show',
      render: (_, show) => (
        <Flex align="center" gap="small">
          <Avatar
            alt={`${show.name} Thumbnail`}
            size={40}
            shape="square"
            src={show.image_url ?? undefined}
            data-testid="show-thumbnail"
          />
          <Typography.Text strong>{show.name}</Typography.Text>
        </Flex>
      ),
    },
    {
      title: 'Estimated earnings',
      key: 'earnings',
      align: 'right',
      render: (_, show) => formatCurrency(show.total?.amount ?? 0, show.total?.currency),
    },
  ];

  return (
    <Table<NetworkShowSummary>
      columns={columns}
      dataSource={data}
      rowKey="id"
      pagination={false}
      showSorterTooltip={false}
      size="small"
    />
  );
};

export default ShowPerformanceCardEcho;
